// MARK://dhk8/pos/stores/auth-store
// PIN-first auth. Phase 1: 6-digit globally-unique account ID. Phase 2: 6-digit
// clave. Falls back to email login. A fresh POS device needs NO tenant context
// before login — the backend resolves the tenant from the pinId and returns it
// in `user.tenantId`; we then persist it (dhk8_tenant cookie + activeTenantId)
// so subsequent data calls carry it.
//
// Test/live ("Option A", Stripe-style): an Account owns a liveTenantId + a
// testTenantId. The active env is client-side — the app sends the live or test
// tenantId in every request (api.ts `getTenantId()`), and `GET /v1/accounts/me`
// returns both. A fresh account defaults to 'test' so nobody bills real ventas
// before they mean to. The choice is persisted in localStorage so a reload keeps
// it. `setActiveEnv()` flips it (the topbar pill calls it).

import { create } from 'zustand'
import type { QueryClient } from '@tanstack/react-query'
import { apiFetch, apiData, svcUrl, setActiveTenantId, setActiveUserId, setActiveBranchId, getActiveBranchId, setStoredJwt, setStoredRefreshJwt, getStoredJwt } from '../lib/api.js'
import { emitTenantChange } from '../hooks/useTenantId.js'
import { PREVIEW_STUB } from '../lib/seed-preview.js'
import { useLicenseStore } from './license-store.js'
import { useLockStore } from './lock-store.js'
import { useThemeStore } from './theme.store.js'

/**
 * Lane #174 — hydrate the per-user theme from backend after login.
 * Fire-and-forget: si el backend no responde (offline) seguimos con
 * localStorage. El store mismo aplica al `<html>` cuando llega data.
 */
async function hydrateThemeAfterLogin(): Promise<void> {
  try {
    await useThemeStore.getState().hydrateFromBackend()
  } catch {
    // ignore — local theme stays in effect
  }
}

// S0-7 hardening 2026-05-24 — Injected QueryClient ref for setActiveEnv to
// invalidate caches when switching test↔live (audit-bleeding C3+C4+C5).
// main.tsx calls `setQueryClient(queryClient)` once after construction.
let _queryClientRef: QueryClient | null = null
export function setQueryClient(qc: QueryClient): void {
  _queryClientRef = qc
}

/**
 * Pull the active tenant's license feature flags so the route gate
 * (`lib/role-license-gate.ts`) has the real `kds`/`reports`/… capabilities
 * before the post-login redirect runs. Best-effort: `refresh()` swallows its
 * own errors and keeps the permissive defaults. Critical for kitchen-role
 * users — without it the license store stays at `kds:false` and the gate
 * bounces them straight back to `/login`.
 */
async function refreshLicense(): Promise<void> {
  try {
    await useLicenseStore.getState().refresh()
  } catch {
    /* refresh() is already best-effort; never let it break login */
  }
}

export interface PosUser {
  id: string
  email: string
  fullName: string
  role: string
  /** Home tenant for this user (the device/session tenant is `activeTenantId()`). */
  tenantId?: string
  branchId: string
  branchIds?: string[]
}

/** Sucursal activa elegida en el selector del avatar. Persistida para sobrevivir
 *  reload (el selector recarga, igual que el switch de entorno). Sólo aplica si
 *  sigue estando entre las sucursales que el backend autoriza (branchIds). */
const BRANCH_LS_KEY = 'dhk8_pos_active_branch'
function readPersistedBranch(): string | null {
  if (typeof window === 'undefined') return null
  try { return window.localStorage.getItem(BRANCH_LS_KEY) } catch { return null }
}
function persistBranch(branchId: string): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(BRANCH_LS_KEY, branchId) } catch { /* ignore */ }
}

/** The user shape dhk8-platform-auth returns on pin-login / login / me. */
type AuthUser = { id: string; email: string; fullName: string; role: string; tenantId?: string; branchId?: string; branchIds?: string[] }
function toPosUser(u: AuthUser): PosUser {
  const branchIds = u.branchIds ?? []
  // Respeta la sucursal elegida en el selector SIEMPRE que el backend la siga
  // autorizando; si no (rol de piso con una sola, o la sucursal ya no existe),
  // cae al default del backend. Nunca deja al usuario en una sucursal que no le
  // corresponde.
  const persisted = readPersistedBranch()
  const branchId = persisted && branchIds.includes(persisted) ? persisted : (u.branchId ?? '')
  return {
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    role: u.role,
    ...(u.tenantId ? { tenantId: u.tenantId } : {}),
    branchId,
    branchIds,
  }
}

export type ActiveEnv = 'test' | 'live'

/** Shape of `GET /v1/accounts/me` (the bits the POS shell needs). */
export interface AccountMe {
  accountId: string
  liveTenantId: string
  testTenantId: string
  /** Optional server hint for the account's last env; the persisted client value wins. */
  activeEnv?: ActiveEnv
  /** Business name (commercial brand) — used in the ticket header. */
  name?: string
  slug?: string
}

interface AuthState {
  user: PosUser | null
  loading: boolean
  error: string | null
  // ── test/live context ──
  accountId: string | null
  /** Business name from /accounts/me — shown in receipt headers. */
  accountName: string | null
  liveTenantId: string | null
  testTenantId: string | null
  activeEnv: ActiveEnv
  /** The currently-active tenantId (live or test depending on `activeEnv`). */
  activeTenantId: () => string | null
  /** Flip the active env (live ↔ test). Llama POST /v1/auth/switch-env que
   *  re-emite JWT scopeado al targetTenantId; reemplaza JWT/userId/branchId
   *  y purga React Query. Async porque hace fetch al backend. */
  setActiveEnv: (env: ActiveEnv) => Promise<void>
  /** Fetch `GET /v1/accounts/me` (with a PREVIEW_STUB fallback) and wire up tenants. */
  fetchAccount: () => Promise<void>
  // ── selector de sucursal ──
  /** Sucursales que el usuario puede operar (nombre + id). Owner/manager: todas
   *  las del tenant; staff de piso: la suya. Se cargan con GET /v1/me/branches. */
  branches: { id: string; name: string }[]
  fetchBranches: () => Promise<void>
  /** Fija la sucursal activa (persiste + recarga para repintar plano/KDS/caja). */
  setActiveBranch: (branchId: string) => void
  // ── auth ──
  hydrate: () => Promise<void>
  pinLogin: (pinId: string, pinPassword: string) => Promise<void>
  emailLogin: (email: string, password: string) => Promise<void>
  logout: () => void
}

const ENV_LS_KEY = 'dhk8_pos_active_env'

function readPersistedEnv(): ActiveEnv {
  // 2026-05-24 hotfix: default 'live'. Antes era 'test' lo cual mandaba al POS
  // a usar testTenantId, que para SAVIs (cliente real) era un placeholder o un
  // tenant test pre-cleanup → la mayoría de queries del POS fallaban con
  // TENANT_MISMATCH o "tenant scope required". El cajero opera real, no test.
  if (typeof window === 'undefined') return 'live'
  try {
    const stored = window.localStorage.getItem(ENV_LS_KEY)
    return stored === 'test' ? 'test' : 'live'
  } catch {
    return 'live'
  }
}

function persistEnv(env: ActiveEnv): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ENV_LS_KEY, env)
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * S0-9 hardening 2026-05-24 — DEPRECATED.
 * El backend dhk8-platform-auth ya setea la cookie `dhk8_token` con
 * HttpOnly+Secure+SameSite=Lax al responder pin-login / login (vía
 * @dockethub/sdk-auth `setAuthCookie`). Si el frontend ADEMÁS reescribe la
 * cookie desde document.cookie, sin HttpOnly, queda exfiltrable por XSS
 * (audit-bleeding C28). Mantengo la función como no-op para no romper
 * call-sites; el browser sigue auto-adjuntando la httpOnly del backend
 * gracias a `credentials:'include'` en api.ts. Eliminar definitivamente
 * una vez se confirme que ningún consumidor read-side lo necesita.
 */
function setTokenCookie(_token: string) {
  // intencionalmente vacío — backend setea httpOnly cookie ya.
  return
}
/**
 * S1-Y hardening 2026-05-24 — DEPRECATED.
 * `dhk8_user` cookie eliminada. El JWT lleva el userId en su claim `sub`
 * y el backend lo extrae sin necesidad de un header X-User-Id. Mantengo
 * la función como no-op para no romper call-sites (publishUser, logout)
 * y para limpiar cookies pre-S1-Y residuales en el browser (max-age=0).
 */
function setUserCookie(_userId: string) {
  // intencionalmente vacío — no más cookies JS-writable de userId.
  return
}
function clearUserCookie() {
  if (typeof document === 'undefined') return
  // Best-effort: limpiar cualquier cookie pre-S1-Y residente en el browser
  // (un usuario logueado antes del deploy todavía la tiene). Después del
  // primer logout post-S1-Y la cookie ya no se vuelve a setear.
  document.cookie = 'dhk8_user=;path=/;max-age=0'
}
/**
 * S1-Y hardening 2026-05-24 — DEPRECATED.
 * `dhk8_tenant` cookie eliminada. El tenant activo vive ahora SOLO en
 * memoria (api.ts.activeTenantOverride + auth-store state). El reload del
 * tab se rehidrata vía /v1/auth/me que devuelve user.tenantId, y la
 * persistencia entre tabs se cubre por el JWT en httpOnly. Mantengo la
 * función como no-op para no romper call-sites (publishTenantFromUser,
 * setActiveEnv) y para limpiar cookies pre-S1-Y residuales.
 */
function setTenantCookie(_tenantId: string) {
  // intencionalmente vacío — no más cookies JS-writable de tenantId.
  return
}
/** Clear the terminal-lock flag (sessionStorage key written by ui-restaurant
 *  lockSession) AND the in-memory lock-store flag. A successful fresh login
 *  (pin-login or email-login) implicitly unlocks — the user just typed their
 *  credentials, no need to ask for the unlock PIN again on the LockOverlay.
 *
 *  Why both: sessionStorage is the *persisted* lock flag (survives reloads),
 *  but the zustand `useLockStore.locked` flag was set in-memory by
 *  `lock-store.lock()` when the terminal locked. Removing the storage key
 *  alone is not enough — the LockOverlay subscribes to the zustand flag and
 *  would still render on top of the just-logged-in shell. Reset both so a
 *  fresh login lands the user inside without a duplicate unlock prompt. */
function clearTerminalLock() {
  try { sessionStorage.removeItem('dhk8.lock') } catch { /* ignore */ }
  try { useLockStore.setState({ locked: false }) } catch { /* ignore */ }
}

function clearTenantCookie() {
  if (typeof document === 'undefined') return
  // S1-Y hardening 2026-05-24: best-effort cleanup de cookie pre-S1-Y
  // residente en el browser. Después del primer logout post-S1-Y ya no
  // se vuelve a setear (setTenantCookie es no-op).
  document.cookie = 'dhk8_tenant=;path=/;max-age=0'
}

/** DEMO PIN pair accepted only in the no-backend preview build (VITE_API_STUB=1). */
const DEMO_PIN_ID = '123456'
const DEMO_PIN_PASSWORD = '654321'
const DEMO_USER: PosUser = {
  id: 'u-demo', email: 'demo@nintaigo.com', fullName: 'Cajero Demo', role: 'cashier',
  tenantId: 'tn-test-preview', branchId: 'br-preview', branchIds: ['br-preview'],
}

/** Publish the acting user (id) downstream + persist a cookie so a reload keeps headers. */
function publishUser(u: PosUser | null): void {
  setActiveUserId(u?.id ?? null)
  // Sólo siembra la default del JWT si el operador no ha elegido sucursal;
  // su elección persiste entre recargas.
  if (getActiveBranchId() === null) setActiveBranchId(u?.branchId ?? null)
  if (u?.id) setUserCookie(u.id)
  else clearUserCookie()
}

/**
 * After a login that resolved a tenant server-side (pin-login / email-login / me),
 * publish it so data calls carry `x-dhk8-tenant-id` even on a brand-new device.
 * `fetchAccount()` may later refine which tenantId is active (live vs test).
 */
function publishTenantFromUser(u: PosUser | null): void {
  if (u?.tenantId) {
    setActiveTenantId(u.tenantId)
    setTenantCookie(u.tenantId)
  }
}

/** Deterministic stub account for the no-backend preview build. */
const PREVIEW_ACCOUNT: AccountMe = {
  accountId: 'acct-preview',
  liveTenantId: 'tn-live-preview',
  testTenantId: 'tn-test-preview',
  activeEnv: 'test',
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** True when `id` is a real tenant uuid (not a `tn-*-preview` placeholder). */
function isRealTenantId(id: string | null | undefined): id is string {
  return typeof id === 'string' && UUID_RE.test(id)
}

/**
 * The `env` claim the backend stamps into the JWT at login (derived from the
 * tenant the PIN resolved to). Authoritative for the live/test label when
 * `/accounts/me` can't resolve the Account: a non-owner staff member (cashier,
 * waiter) gets a 404 ("Account not found for user" — `accounts.service.ts`
 * resolves by `ownerUserId`), so the fallback collapses live===test into the
 * single tenant the PIN gave us. With both slots equal the tenant id can no
 * longer tell live from test, so we read the env straight from the JWT.
 * No verificamos la firma — esto es solo para la etiqueta de UI; el backend
 * ya scopeó el JWT al tenant correcto y enforcea de verdad.
 */
function jwtEnvClaim(): ActiveEnv | null {
  const tok = getStoredJwt()
  if (!tok) return null
  try {
    const part = tok.split('.')[1]
    if (!part) return null
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { env?: unknown }
    return payload.env === 'live' || payload.env === 'test' ? payload.env : null
  } catch {
    return null
  }
}

/**
 * Account fallback for a tenant that has no Account row in dhk8-platform-tenants
 * (e.g. provider tenants like Café del Sur — `/accounts/me` → 404). We must NOT
 * fall back to `PREVIEW_ACCOUNT` here: its `tn-*-preview` tenant ids are not
 * uuids, so every downstream service rejects them ("Invalid uuid"). Instead we
 * mirror the *real* tenant the login already resolved (`user.tenantId`) into
 * both env slots so the env-switcher still works and data calls stay valid.
 */
function accountFromUserTenant(tenantId: string): AccountMe {
  return {
    accountId: `tenant:${tenantId}`,
    liveTenantId: tenantId,
    testTenantId: tenantId,
    activeEnv: 'test',
  }
}

/** Resolve + publish the active tenantId from a state slice. Returns the value. */
function publishTenant(s: { activeEnv: ActiveEnv; liveTenantId: string | null; testTenantId: string | null }): string | null {
  const id = s.activeEnv === 'live' ? s.liveTenantId : s.testTenantId
  setActiveTenantId(id)
  return id ?? null
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,

  accountId: null,
  accountName: null,
  liveTenantId: null,
  testTenantId: null,
  activeEnv: readPersistedEnv(),

  activeTenantId() {
    const { activeEnv, liveTenantId, testTenantId } = get()
    return activeEnv === 'live' ? liveTenantId : testTenantId
  },

  async setActiveEnv(env) {
    if (env === get().activeEnv) return
    // 2026-05-24 fix tenant-mismatch real — llamar backend POST /v1/auth/switch-env
    // que re-emite el JWT scopeado al targetTenant. Sin esto, cambiar solo el
    // tenantId que se manda en queries dispara backend tenantGuard 403
    // ("query/header/body tenantId does not match auth tenantId") porque el
    // JWT actual fue emitido para el tenant SOURCE.
    //
    // PREVIEW_STUB: no hay backend, mantén el comportamiento puramente client.
    if (PREVIEW_STUB) {
      persistEnv(env)
      const newTenant = env === 'live' ? get().liveTenantId : get().testTenantId
      if (newTenant) setTenantCookie(newTenant)
      publishTenant({ activeEnv: env, liveTenantId: get().liveTenantId, testTenantId: get().testTenantId })
      emitTenantChange()
      set({ activeEnv: env })
      if (_queryClientRef) _queryClientRef.removeQueries()
      return
    }
    // Llama el endpoint con Bearer del JWT actual. El backend valida que ambos
    // tenants pertenezcan al mismo Account (Stripe-style), busca el user clone
    // por email en el target tenant, crea session + refresh + accessToken nuevos.
    // Usa apiData (no apiFetch) porque el backend devuelve envelope
    // { ok: true, data: { token, refreshToken, user, env, tenantId } }; apiData
    // lo desempaqueta. Si usamos apiFetch obtenemos resp.token === undefined
    // y setStoredJwt(undefined) BORRA el JWT del localStorage (regression).
    type SwitchEnvResp = {
      token: string
      refreshToken?: string
      user: { id: string; email: string; fullName: string; tenantId: string; role: string; roles?: string[]; branchId: string; branchIds?: string[] }
      env?: ActiveEnv
      tenantId?: string
    }
    let resp: SwitchEnvResp
    try {
      resp = await apiData<SwitchEnvResp>(
        svcUrl('dhk8-platform-auth', '/v1/auth/switch-env'),
        { method: 'POST', body: { targetEnv: env } },
      )
    } catch (err) {
      // FORBIDDEN si no hay user clone en target (account sin entorno test
      // provisionado, p.ej.) o si los tenants no comparten Account.
      // Re-throw para que la UI muestre toast; NO modificamos el state.
      throw err
    }
    // Guard: si el backend respondió OK pero sin token, NO escribimos
    // localStorage — destruir el JWT actual sería peor que no haber cambiado.
    if (typeof resp.token !== 'string' || resp.token.length === 0) {
      throw new Error('switch-env: respuesta sin token')
    }
    // Persiste el JWT nuevo + actualiza activeUserId/activeTenantId para que
    // api.ts mande los headers correctos (Authorization Bearer + x-dhk8-tenant-id).
    setStoredJwt(resp.token)
    if (typeof resp.refreshToken === 'string' && resp.refreshToken.length > 0) {
      setStoredRefreshJwt(resp.refreshToken)
    }
    const newUser = toPosUser({
      id: resp.user.id,
      email: resp.user.email,
      fullName: resp.user.fullName,
      role: resp.user.role,
      tenantId: resp.user.tenantId,
      branchId: resp.user.branchId,
      branchIds: resp.user.branchIds ?? (resp.user.branchId ? [resp.user.branchId] : []),
    })
    publishUser(newUser)
    setActiveTenantId(resp.user.tenantId)
    setTenantCookie(resp.user.tenantId)
    persistEnv(env)
    // El user del target tenant ya es el correcto — actualizamos también el
    // estado in-memory del store (user + activeEnv).
    set({ user: newUser, activeEnv: env })
    emitTenantChange()
    if (_queryClientRef) {
      // removeQueries para drop inmediato del cache del env anterior; las
      // nuevas requests se hacen con el tenant + JWT nuevos.
      _queryClientRef.removeQueries()
    }
  },

  branches: [],

  async fetchBranches() {
    if (PREVIEW_STUB) {
      set({ branches: [{ id: 'br-preview', name: 'Sucursal demo' }] })
      return
    }
    try {
      // /v1/me/branches → { ok, data: Branch[] }, scopeado al tenant del JWT.
      const raw = await apiFetch<{ ok?: boolean; data?: { id: string; name: string }[] }>(
        svcUrl('dhk8-platform-tenants', '/v1/me/branches'),
      )
      const list = (raw?.data ?? []).map((b) => ({ id: b.id, name: b.name }))
      set({ branches: list })
    } catch {
      // Sin red: el selector queda vacío y el POS sigue con la sucursal actual.
      set({ branches: [] })
    }
  },

  setActiveBranch(branchId: string) {
    const user = get().user
    if (!user || branchId === user.branchId) return
    // Sólo permite fijar una sucursal que el backend autoriza (branchIds del JWT).
    if (!user.branchIds?.includes(branchId)) return
    persistBranch(branchId)
    // Recarga dura, igual que el switch de entorno: muchas vistas (plano, KDS,
    // caja, reportes) leen user.branchId en un useEffect y no reaccionan a un
    // cambio de estado sin remount. El branchId persistido se re-aplica en
    // toPosUser al rehidratar, así que la sesión sobrevive.
    set({ user: { ...user, branchId } })
    if (typeof window !== 'undefined') window.location.reload()
  },

  async fetchAccount() {
    // The real tenant the login already resolved — used as the fallback when
    // `/accounts/me` has no row for this tenant (provider tenants), so we never
    // publish a non-uuid placeholder that breaks every downstream service.
    const userTenant = get().user?.tenantId ?? null

    let acct: AccountMe | null = null
    if (PREVIEW_STUB) {
      acct = PREVIEW_ACCOUNT
    } else {
      try {
        // /v1/accounts/me returns the standard envelope { ok: true, data: AccountMe }
        // (apiFetch returns the raw json — does NOT auto-unwrap). Some legacy code
        // paths nested an `account` key as well; we tolerate both.
        const raw = await apiFetch<{ ok?: boolean; data?: AccountMe; account?: AccountMe } & Partial<AccountMe>>(
          svcUrl('dhk8-platform-tenants', '/v1/accounts/me'),
        )
        const inner = raw && typeof raw === 'object'
          ? (raw.data as AccountMe | undefined) ?? (raw.account as AccountMe | undefined) ?? (raw as AccountMe)
          : null
        acct = inner ?? null
      } catch {
        // `/accounts/me` is missing for this tenant (404 — e.g. a provider
        // tenant with no Account row). Fall back to the *real* logged-in
        // tenant, NOT the preview stub: the stub's `tn-*-preview` ids are not
        // uuids and would make every data call fail with "Invalid uuid".
        acct = isRealTenantId(userTenant) ? accountFromUserTenant(userTenant) : null
      }
    }
    if (!acct) return
    // Defensive: if the account came back with placeholder / non-uuid tenant
    // ids but we DO know the real tenant, prefer the real one.
    if (!PREVIEW_STUB && isRealTenantId(userTenant)) {
      if (!isRealTenantId(acct.liveTenantId)) acct = { ...acct, liveTenantId: userTenant }
      if (!isRealTenantId(acct.testTenantId)) acct = { ...acct, testTenantId: userTenant }
    }
    // 2026-05-24 — el localStorage activeEnv puede tener 'test' residual de
    // una sesión previa. Si el user actual (JWT) NO está en el target tenant,
    // el switch real se hará en background via setActiveEnv (que llama backend
    // re-emit JWT). Pero si el JWT actual ya es del target tenant correcto
    // (mismo user, mismo tenantId), respetamos la persistencia tal cual.
    // Si los tenants son iguales (live === test, env solo diferenciada por
    // flag fiscal sandbox), `setActiveEnv` es no-op en backend pero válido.
    const persistedEnv = readPersistedEnv()
    // Coherencia: si el persistido es 'test' pero el JWT current está en el
    // liveTenantId (el caso común tras login fresh), respetamos 'live' hasta
    // que el usuario explícitamente flippee el toggle (que dispara backend
    // switch-env). Sin esto, el shell muestra "test" pero las queries usan
    // liveTenantId y se confunde la UX.
    const env: ActiveEnv = (() => {
      const userTid = get().user?.tenantId
      // Cuenta real (live≠test): el tenant del PIN nos dice en cuál estamos.
      if (acct.liveTenantId !== acct.testTenantId) {
        if (userTid === acct.testTenantId) return 'test'
        if (userTid === acct.liveTenantId) return 'live'
      }
      // Ambiguo: live===test — pasa cuando `/accounts/me` dio 404 (staff que no
      // es dueño de la cuenta) y el fallback colapsó ambos slots al único tenant
      // del PIN. El tenant id ya no distingue live de test, así que tomamos el
      // claim `env` del JWT (que el backend estampó al login). Sin esto el
      // chequeo `userTid === testTenantId` ganaba siempre → etiqueta "Pruebas"
      // aunque la sesión fuese Vivo.
      const jwtEnv = jwtEnvClaim()
      if (jwtEnv) return jwtEnv
      // fallback final: respeta persisted (user en ningún tenantId conocido —
      // ej. impersonate o tenants placeholder, y sin claim env en el JWT).
      return persistedEnv
    })()
    set({ accountId: acct.accountId, accountName: acct.name ?? null, liveTenantId: acct.liveTenantId, testTenantId: acct.testTenantId, activeEnv: env })
    publishTenant({ activeEnv: env, liveTenantId: acct.liveTenantId, testTenantId: acct.testTenantId })
    // Carga las sucursales del selector — todos los caminos de login pasan por
    // aquí, así el avatar las tiene sin enganchar cada sitio. Best-effort.
    void get().fetchBranches()
  },

  async hydrate() {
    set({ loading: true, error: null })
    if (PREVIEW_STUB) {
      // Stay logged out in the showcase until a PIN is entered.
      set({ user: null, loading: false })
      return
    }
    try {
      // GET /v1/auth/me → AuthUser (Bearer). dhk8-platform-auth is reached via the
      // ingress route /v1/dhk8-platform-auth/* → service sees /v1/auth/me.
      const user = toPosUser(await apiFetch<AuthUser>(svcUrl('dhk8-platform-auth', '/v1/auth/me')))
      publishUser(user)
      publishTenantFromUser(user)
      set({ user, loading: false })
      await get().fetchAccount()
      await refreshLicense()
    } catch {
      publishUser(null)
      set({ user: null, loading: false })
    }
  },

  async pinLogin(pinId, pinPassword) {
    set({ loading: true, error: null })
    if (PREVIEW_STUB) {
      if (pinId === DEMO_PIN_ID && pinPassword === DEMO_PIN_PASSWORD) {
        publishUser(DEMO_USER)
        set({ user: DEMO_USER, loading: false })
        clearTerminalLock()
        await get().fetchAccount()
        return
      }
      set({ loading: false, error: 'PIN incorrecto' })
      throw new Error('PIN incorrecto')
    }
    try {
      // POST /v1/auth/pin-login { pinId, pinPassword } — NO X-Tenant-Id needed:
      // the pinId is globally unique, the backend returns user.tenantId. apiFetch
      // still forwards an existing dhk8_tenant cookie if the device is already paired.
      const data = await apiFetch<{ token: string; refreshToken: string; user: AuthUser }>(
        svcUrl('dhk8-platform-auth', '/v1/auth/pin-login'),
        { method: 'POST', body: { pinId, pinPassword } },
      )
      // 2026-05-24 hotfix: persistir JWT en localStorage para Bearer header
      // (la cookie httpOnly no viaja cross-origin pos.nintaigo.com→api.nintaigo.com).
      setStoredJwt(data.token)
      setStoredRefreshJwt(data.refreshToken)
      setTokenCookie(data.token)
      const user = toPosUser(data.user)
      publishUser(user)
      publishTenantFromUser(user)
      set({ user, loading: false })
      // The PIN typed at the login screen is sufficient credential for the device
      // — clear any persisted terminal-lock flag so the LockOverlay doesn't ask
      // for the same PIN a second time.
      clearTerminalLock()
      await get().fetchAccount()
      // Load the tenant license BEFORE LoginPage navigates — the route gate
      // needs the real `kds` flag (kitchen-role users land on `/kds`).
      await refreshLicense()
      // Lane #174 — hidratar theme per-usuario (sigue al login). No bloquea.
      void hydrateThemeAfterLogin()
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'PIN incorrecto' })
      throw err
    }
  },

  async emailLogin(email, password) {
    set({ loading: true, error: null })
    if (PREVIEW_STUB) {
      publishUser(DEMO_USER)
      set({ user: DEMO_USER, loading: false })
      clearTerminalLock()
      await get().fetchAccount()
      return
    }
    try {
      // POST /v1/auth/login { email, password } → { token, refreshToken, user }.
      const data = await apiFetch<{ token: string; refreshToken: string; user: AuthUser }>(
        svcUrl('dhk8-platform-auth', '/v1/auth/login'),
        { method: 'POST', body: { email, password } },
      )
      // 2026-05-24 hotfix: persistir JWT en localStorage para Bearer header.
      setStoredJwt(data.token)
      setStoredRefreshJwt(data.refreshToken)
      setTokenCookie(data.token)
      const user = toPosUser(data.user)
      publishUser(user)
      publishTenantFromUser(user)
      set({ user, loading: false })
      clearTerminalLock()
      await get().fetchAccount()
      await refreshLicense()
      // Lane #174 — hidratar theme per-usuario (sigue al login).
      void hydrateThemeAfterLogin()
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Credenciales inválidas' })
      throw err
    }
  },

  logout() {
    // S0-9 hardening: la cookie `dhk8_token` es HttpOnly (la setea el backend),
    // así que JS NO puede sobrescribirla. Pedimos al backend que la limpie
    // (POST /api/auth/logout limpia ambos httpOnly cookies). Best-effort: si
    // el call falla (red, etc.) limpiamos al menos los cookies no-httpOnly y
    // el state local para que el reload muestre /login.
    void fetch(svcUrl('dhk8-platform-auth', '/api/auth/logout'), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => { /* best-effort */ })
    // Forget the last PIN id so the next user doesn't land on the PASS phase
    // pre-filled with someone else's id. Mirrors LoginPage's LAST_PINID_KEY.
    if (typeof window !== 'undefined') {
      try { window.localStorage.removeItem('dhk8-pos-last-pinid') } catch { /* noop */ }
    }
    // 2026-05-24 hotfix: limpiar JWT persistido + refresh.
    setStoredJwt(null)
    setStoredRefreshJwt(null)
    clearUserCookie()
    clearTenantCookie()
    setActiveTenantId(null)
    setActiveUserId(null)
    setActiveBranchId(null)
    // Drop any stale terminal-lock flag too. If the user logs out while the
    // terminal is locked (e.g. via a session-restore path), the lock-store's
    // in-memory `locked` flag would otherwise survive the next fresh login and
    // pop up a duplicate unlock prompt on top of the freshly-authenticated UI.
    clearTerminalLock()
    set({ user: null, accountId: null, accountName: null, liveTenantId: null, testTenantId: null })
  },
}))
